import React, { useState, useRef, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch } from 'react-redux';

import { default as MuiAvatarGroup } from '@material-ui/lab/AvatarGroup';

import readFile from '../../utils/readFile';

import { UploadImageDialog, FilledIconButton, CameraIcon, Tooltip } from '../index';

import { StyledAvatar } from './style';

import { alertError } from '../../redux/stateActions';

const arrColors = [
    'blue1',
    'blue2',
    'dimBlue',
    'purple',
    'pink',
    'green',
    'greenLight',
    'primaryBlue',
    'primaryLightBlue',
    'primaryDarkBlue',
    'primaryDarkPurple',
    'primaryPurple',
    'primaryLightPurple',
    'primaryLightPink',
];

export const Avatar = ({
    alt,
    src,
    newImage,
    variant,
    deactivated,
    onClick,
    disabled,
    noBackgroundColor,
    ...rest
}) => {
    const [newAlt, setNewAlt] = useState('');
    const [backgroundColor] = useState(_.random(0, arrColors.length + 1));
    useEffect(() => {
        if (!alt) return;
        const arr = alt.split(' ');
        if (arr.length > 1) {
            setNewAlt(`${arr[0].charAt(0)}${arr[1].charAt(0)}`);
        } else {
            setNewAlt(`${arr[0].charAt(0)}`);
        }
    }, [alt]);

    return (
        <StyledAvatar
            {...rest}
            src={newImage ? newImage : src ? src : null}
            variant={variant ? variant : 'square'}
            $backgroundColor={!noBackgroundColor && arrColors[backgroundColor]}
            deactivated={deactivated}
            onClick={() => {
                if (disabled || !onClick) return;
                onClick();
            }}
            disabled={disabled}
        >
            {newAlt}
        </StyledAvatar>
    );
};

Avatar.propTypes = {
    alt: PropTypes.string,
    src: PropTypes.string,
    rest: PropTypes.object,
    newImage: PropTypes.string,
    variant: PropTypes.string,
    deactivated: PropTypes.bool,
    disabled: PropTypes.bool,
};

export const AvatarWithUploader = ({
    setNewAvatar,
    setNewFileInfo,
    $avatarSize,
    alt,
    src,
    $styleButton,
    parentReset,
    disabled,
    deactivated,
    ...rest
}) => {
    const dispatch = useDispatch();

    const [newFile, setNewFile] = useState(null);
    const [showUploadImageDialog, setShowUploadImageDialog] = useState(false);
    const [newImage, setNewImage] = useState(null);

    useEffect(() => {
        if (parentReset) {
            reset();
        }
    }, [parentReset]);

    const reset = () => {
        setNewFile(null);
        setShowUploadImageDialog(false);
        setNewImage(null);
    };

    const inputRef = useRef(null);

    const handleClick = () => {
        inputRef.current.click();
    };

    const onFileChange = async (event) => {
        const newFile = event.target.files[0];
        event.target.value = null;

        if (!['image/png', 'image/jpeg'].includes(newFile.type)) {
            return dispatch(alertError('invalid file type.'));
        }

        if (newFile.size > 2097152) {
            return dispatch(alertError('file size limit exceeded 2MB.'));
        }

        if (!_.isUndefined(setNewFileInfo)) setNewFileInfo(newFile);
        setNewFile(await readFile(newFile));
        setShowUploadImageDialog(true);
    };

    const onCropImage = (file) => {
        setNewImage(file);
        if (!_.isUndefined(setNewAvatar)) setNewAvatar(file);
    };

    return (
        <div
            style={{
                position: 'relative',
                width: $avatarSize,
                height: $avatarSize,
            }}
        >
            <UploadImageDialog
                open={showUploadImageDialog}
                onClose={() => {
                    setShowUploadImageDialog(false);
                    setNewFile(null);
                }}
                image={newFile}
                onCropImage={onCropImage}
            />
            <input
                ref={inputRef}
                type="file"
                onChange={onFileChange}
                accept=".jpeg,.jpg,.png"
                style={{ display: 'none' }}
            />
            <Avatar
                {...rest}
                alt={alt}
                src={newImage || src}
                newImage={newImage}
                deactivated={deactivated}
            />

            {!disabled && (
                <div
                    style={{
                        position: 'absolute',
                        right: 0,
                        bottom: 0,
                        ...$styleButton,
                    }}
                >
                    <Tooltip title="Change profile image" arrow placement="bottom">
                        <div>
                            <FilledIconButton
                                $backgroundColor="purple"
                                onClick={handleClick}
                                style={{ margin: 0 }}
                            >
                                <CameraIcon />
                            </FilledIconButton>
                        </div>
                    </Tooltip>
                </div>
            )}
        </div>
    );
};

AvatarWithUploader.propTypes = {
    setNewAvatar: PropTypes.func,
    setNewFileInfo: PropTypes.func,
    $avatarSize: PropTypes.string,
    alt: PropTypes.string,
    src: PropTypes.string,
    $styleButton: PropTypes.object,
    disabled: PropTypes.bool,
    rest: PropTypes.object,
    parentReset: PropTypes.number,
    deactivated: PropTypes.bool,
};

const useStyles = makeStyles((theme) => ({
    group: ({ $size }) => ({
        '& > .MuiAvatar-colorDefault': {
            color: theme.palette.common.white,
            backgroundColor: _.random(0, arrColors.length + 1),
            fontSize: '12px',
            width: $size ? $size : '35px',
            height: $size ? $size : '35px',
        },
    }),
    avatar: ({ $size }) => ({
        border: `2px solid ${theme.palette.common.white}`,
        color: theme.palette.common.white,
        backgroundColor: _.random(0, arrColors.length + 1),
        width: $size ? $size : '35px',
        height: $size ? $size : '35px',
    }),
}));

export const AvatarGroup = ({ max, $size, data, style }) => {
    const classes = useStyles({ $size });

    return (
        <MuiAvatarGroup max={max || 5} className={classes.group} style={style}>
            {!_.isEmpty(data) &&
                data.map((item) => (
                    <Avatar
                        variant="circular"
                        $avatarSize={$size ? $size : '35px'}
                        $borderRadius="50%"
                        key={'AvatarGroup' + item?.id}
                        alt={
                            !item.id || item?.deactivated || !item?.user ? '' : item?.user?.fullname
                        }
                        src={
                            !item.id || item?.deactivated || !item?.user
                                ? null
                                : item?.user?.avatar?.url
                        }
                        className={classes.avatar}
                    />
                ))}
        </MuiAvatarGroup>
    );
};

AvatarGroup.propTypes = {
    data: PropTypes.array,
    max: PropTypes.number,
    $size: PropTypes.string,
    style: PropTypes.object,
};
